'use client';

import React, { useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import supabase from '@/lib/supabase';
import { Turnstile } from '@marsidev/react-turnstile';
import styles from '../../styles/login.module.css';
import Navbar from '@components/Navbar';
import Footer from '@components/footer/Footer';

export default function LoginPageClient() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const [captchaError, setCaptchaError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const turnstileRef = useRef(null);

  // Check for error parameters in URL on mount
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');
      
      if (error) {
        const errorMessages = {
          'oauth_error': 'OAuth authentication failed. Please try again.',
          'exchange_failed': 'Failed to complete authentication. Please try again.',
          'user_error': 'Failed to retrieve user information. Please try again.',
          'user_creation_failed': 'Failed to create user account. Please contact support if this persists.',
          'user_creation_exception': 'An error occurred creating your account. Please try again.'
        };
        
        setErrorMessage(errorMessages[error] || 'An authentication error occurred. Please try again.');
        
        // Clear the error from URL after displaying
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(''); // Clear any existing errors
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken }
      });
      if (error) throw error;

      // Removed unnecessary POST request that was causing 405 errors
      router.push('/');

    } catch (error) {
      console.error('Login error:', error.message);
      setErrorMessage('Login failed: ' + error.message);
      // Reset CAPTCHA after failed attempt
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setCaptchaToken('');
    }
  };;


  const handleGoogleSignIn = async () => {
    setErrorMessage(''); // Clear any existing errors
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'email profile',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        console.error('OAuth error:', error);
        setErrorMessage('Failed to sign in with Google. Please try again.');
      }
      // If successful, browser will redirect - no need to handle success
    } catch (err) {
      console.error('OAuth exception:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setForgotPasswordMessage('');

    if (!email) {
      setErrorMessage('Please enter your email address first');
      return;
    }

    if (!captchaToken) {
      setErrorMessage('Please complete the CAPTCHA verification first');
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/confirm?next=/reset-password`,
        captchaToken,
      });

      if (error) {
        setErrorMessage(error.message);
      } else {
        setForgotPasswordMessage('Check your email for password reset link');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
      // Reset CAPTCHA after attempt (success or failure)
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setCaptchaToken('');
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <Navbar />
      </header>

      {/* Login Section */}
      <div className={styles.loginContainer}>
        {/* Welcome Section */}
        <div className={styles.welcomeSection}>
          <h1>Welcome to PenguinLearn</h1>
        </div>

        {/* Login Content */}
        <div className={styles.loginContent}>
          {/* Login Form */}
          <div className={styles.loginForm}>
            {/* Error Message Display */}
            {errorMessage && (
              <div style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                color: '#991b1b',
                padding: '0.75rem 1rem',
                borderRadius: '0.375rem',
                marginBottom: '1rem',
                fontSize: '0.875rem'
              }}>
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Input Fields */}
              <div className={styles.loginGroup}>
                <input
                  type="email"
                  placeholder="Email or Username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className={styles.inputField}
                />
                <input
                  type="password"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className={styles.inputField}
                />
                <a
                  href="#"
                  onClick={handleForgotPassword}
                  className={styles.forgotPasswordLink}
                  style={{ pointerEvents: forgotPasswordLoading ? 'none' : 'auto', opacity: forgotPasswordLoading ? 0.6 : 1 }}
                >
                  {forgotPasswordLoading ? 'Sending reset link...' : 'Forgot your password?'}
                </a>
                {forgotPasswordMessage && (
                  <div style={{
                    backgroundColor: '#d1fae5',
                    border: '1px solid #10b981',
                    color: '#065f46',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.375rem',
                    marginTop: '0.5rem',
                    fontSize: '0.875rem'
                  }}>
                    {forgotPasswordMessage}
                  </div>
                )}

                {/* CAPTCHA Verification */}
                <div style={{ marginBottom: '1rem' }}>
                  {captchaLoading && (
                    <div style={{ height: '65px', backgroundColor: '#f3f4f6', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                  )}
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => {
                      setCaptchaToken(token);
                      setCaptchaError('');
                      setCaptchaLoading(false);
                    }}
                    onError={() => {
                      setCaptchaError('CAPTCHA verification failed. Please try again.');
                      setCaptchaLoading(false);
                    }}
                    onExpire={() => {
                      setCaptchaToken('');
                      setCaptchaError('CAPTCHA expired. Please verify again.');
                    }}
                  />
                  {captchaError && (
                    <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>{captchaError}</p>
                  )}
                </div>

                <button type="submit" className={styles.loginBtn} disabled={!captchaToken || captchaLoading}>
                  Log In
                </button>
              </div>

              {/* Divider */}
              <div className={styles.divider}>
                <span>OR</span>
              </div>

              {/* Social Login Button */}
              <div className={styles.socialButtons}>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className={`${styles.socialBtn} ${styles.googleBtn}`}
                >
                  <img src="/images/google.png" alt="Google" className={styles.socialIcon} /> Sign in with Google
                </button>
              </div>

              {/* Sign-Up Prompt */}
              <div className={styles.signupPrompt}>
                Don&apos;t have a PenguinLearn account?{' '}
                <a href="/register" className={styles.signupLink}>
                  Sign up
                </a>
              </div>

              {/* Terms and Privacy Disclaimer */}
              <div className={styles.termsDisclaimer}>
                By clicking Log In or Continue with Google, you agree to PenguinLearn&apos;{' '}
                <a href="/footer/terms" className={styles.termsLink}>
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/footer/privacy-policy" className={styles.privacyLink}>
                  Privacy Policy
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
